import { useCallback, useMemo } from 'react';

import type { Group } from '@bugbug/core/types/groups';
import type { Test } from '@bugbug/core/types/tests';
import { useAppDispatch } from '~/modules/store';
import { UIStateActions } from '~/modules/uiState/uiState.redux';
import { ACTIONS } from '~/services/toasts/internalServerError';

import { testsApi } from '../../tests.api';
import { showTestError } from '../../tests.utils';
import { usePlaybackCursorPosition } from '../usePlaybackCursorPosition/usePlaybackCursorPosition';

export const useDuplicateGroupInTest = () => {
  const dispatch = useAppDispatch();
  const [duplicateGroup, duplicateGroupRequest] = testsApi.useDuplicateGroupMutation();
  const { refreshPlaybackCursorPosition } = usePlaybackCursorPosition();

  const handleDuplicateGroup = useCallback(
    async ({ id, testId }: { id: Group['id']; testId: Test['id'] }) => {
      const result = duplicateGroup({ id, testId }).unwrap();

      result
        .then((group) => {
          dispatch(
            UIStateActions.addRecentlyAddedStepsIds({
              stepsIds: group.steps.map((step) => step.id),
            }),
          );
          refreshPlaybackCursorPosition();
        })
        .catch((error) => {
          showTestError(ACTIONS.CLONE, error);
        });
    },
    [dispatch, duplicateGroup, refreshPlaybackCursorPosition],
  );

  return useMemo(
    () => [handleDuplicateGroup, duplicateGroupRequest] as const,
    [handleDuplicateGroup, duplicateGroupRequest],
  );
};
